import { useGetTasksQuery } from 'api/collaborateApi';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  CollaborateTaskData,
  GetTasksParams,
  TaskStatus,
  TaskType,
} from 'types/api/collaborate';
import {
  getHostAndPath,
  getPageUrl,
  getUrlParams,
  isInIframe,
} from 'utils/proxyUtils';
import {
  getNotifiedUsersFilter,
  getOrderByFilter,
  getPageFilter,
  getTaskStatusFilter,
  getTaskTypesFilter,
  getTextSearchFilter,
} from '../store/filtersSlice';
import { getSelectedSiteInfo, getSiteTotalTasks } from '../store/siteInfoSlice';
import { getUser } from '../store/userSlice';

interface UseTasksOptions {
  siteId?: string;
  workspaceId?: number;
  forCurrentPageOnly?: boolean;
  forAllTasksOnly?: boolean;
  skipQuery?: boolean;
  filterGeneralTasks?: boolean;
  applyFilters?: boolean; // Controls whether to apply filters
  simpleMode?: boolean; // New option for simplified parameter passing
  currentPageUrl?: string; // Optional override for current page URL
  floatingPanel?: boolean; // New option for floating panel mode
  limit?: number;
  offset?: number;
}

const roundUpToNearest10 = (value?: number): number => {
  if (value === undefined || value < 0) {
    return 10;
  }
  return Math.ceil(value / 10) * 10;
};

// Default task statuses to show when no specific filter is applied
const DEFAULT_TASK_STATUSES: TaskStatus[] = [
  'in-progress',
  'open',
  'pending-review',
];

/**
 * Helper function to determine which task statuses to include in the query
 */
const getTaskStatusesForQuery = (
  taskStatusFilter?: TaskStatus | TaskStatus[]
): TaskStatus[] => {
  if (!taskStatusFilter) {
    // No filter applied - return default statuses
    return DEFAULT_TASK_STATUSES;
  }

  if (Array.isArray(taskStatusFilter)) {
    return taskStatusFilter;
  }

  // Single status selected
  if (taskStatusFilter === 'complete') {
    // When complete is selected, show all statuses (including complete)
    return [...DEFAULT_TASK_STATUSES, 'complete'];
  }

  // For any other single status, include it with defaults
  return DEFAULT_TASK_STATUSES.includes(taskStatusFilter)
    ? DEFAULT_TASK_STATUSES
    : [...DEFAULT_TASK_STATUSES, taskStatusFilter];
};

/**
 * Helper function to determine which task types to include in the query
 */
const getTaskTypesForQuery = (
  taskTypesFilter?: TaskType | TaskType[]
): TaskType[] | undefined => {
  if (!taskTypesFilter) {
    return undefined; // No filtering by task type
  }

  if (Array.isArray(taskTypesFilter)) {
    return taskTypesFilter;
  }

  // Single task type selected
  return [taskTypesFilter];
};

export const useTasks = (options: UseTasksOptions = {}) => {
  // Set defaults
  const applyFilters = options.applyFilters !== false; // Default to true unless explicitly set to false
  const simpleMode = options.simpleMode === true; // Default to false unless explicitly set to true
  const floatingPanel = options.floatingPanel === true; // Default to false unless explicitly set to true

  // Get site info if siteId isn't provided
  const siteInfo = useSelector(getSelectedSiteInfo);
  const siteId = options.siteId || (siteInfo?.id as string);
  const userInfo = useSelector(getUser);
  const totalTasksRaw = useSelector(getSiteTotalTasks);
  const offset = options.offset || 0;
  const totalTasks = roundUpToNearest10(totalTasksRaw);

  // Get workspace ID
  const workspaceId = options.workspaceId || siteInfo?.workspace_id;

  // Get current page URL
  const defaultCurrentPageUrl = getPageUrl() || '';
  const currentPageUrl = options.currentPageUrl || defaultCurrentPageUrl;

  // Get all filter values from Redux store (only if not in simple mode)
  const pageFilter = useSelector(getPageFilter);
  const notifiedUsers = useSelector(getNotifiedUsersFilter);
  const orderBy = useSelector(getOrderByFilter);
  const taskStatusFilter = useSelector(getTaskStatusFilter);
  const taskTypesFilter = useSelector(getTaskTypesFilter);
  const textSearch = useSelector(getTextSearchFilter);

  // Process task status and task types for the query
  const taskStatusForQuery = useMemo(() => {
    return applyFilters
      ? getTaskStatusesForQuery(taskStatusFilter)
      : DEFAULT_TASK_STATUSES;
  }, [applyFilters, taskStatusFilter]);

  const taskTypesForQuery = useMemo(() => {
    return applyFilters ? getTaskTypesForQuery(taskTypesFilter) : undefined;
  }, [applyFilters, taskTypesFilter]);

  // Get URL parameters for initial tasks
  const searchParams = getUrlParams();
  const hasInitialTasksParam =
    searchParams.get('new') === 'true' || searchParams.get('new') === '1';
  const getInitialTasks =
    hasInitialTasksParam &&
    (options.forCurrentPageOnly ? !userInfo?.loggedIn : true);

  // Prepare basic query parameters (siteId only)
  const basicQueryParams: GetTasksParams = {
    siteId,
    task_status: taskStatusForQuery, // Always include default statuses
  };

  const floatingPanelParams: GetTasksParams = hasInitialTasksParam
    ? {
        siteId,
        get_initial_tasks: getInitialTasks,
        limit: totalTasks,
        offset,
        task_status: taskStatusForQuery,
      }
    : {
        siteId,
        workspace_id: workspaceId,
        current_page_url: currentPageUrl,
        limit: totalTasks,
        offset,
        task_status: taskStatusForQuery,
      };

  // Prepare simple mode parameters (siteId, workspace, current page only)
  const simpleQueryParams: GetTasksParams = {
    siteId,
    workspace_id: workspaceId,
    current_page_url: options.forCurrentPageOnly ? currentPageUrl : undefined,
    get_initial_tasks: getInitialTasks,
    limit: totalTasks,
    offset,
    task_status: taskStatusForQuery,
  };

  // Prepare full query parameters (with all filters)
  const fullQueryParams: GetTasksParams = {
    current_page_url:
      pageFilter === 'current' || options.forCurrentPageOnly
        ? currentPageUrl
        : undefined,
    notified_users: notifiedUsers,
    order_by: orderBy,
    task_status: taskStatusForQuery,
    task_types: taskTypesForQuery,
    text_search: textSearch,
    workspace_id: workspaceId,
    get_initial_tasks: getInitialTasks,
    limit: totalTasks,
    siteId,
    offset,
  };

  // Choose which query params to use based on mode and filter settings
  let queryParams: GetTasksParams;
  if (floatingPanel) {
    queryParams = floatingPanelParams;
  } else if (simpleMode) {
    queryParams = simpleQueryParams;
  } else if (applyFilters) {
    queryParams = fullQueryParams;
  } else {
    queryParams = basicQueryParams;
  }

  // Determine if query should be skipped
  const shouldSkip =
    !siteId ||
    options.skipQuery ||
    (applyFilters &&
      !currentPageUrl &&
      (pageFilter === 'current' || options.forCurrentPageOnly));

  // Execute the query
  const query = useGetTasksQuery(queryParams, { skip: shouldSkip });

  // Check if a task is on the current page
  const isTaskOnCurrentPage = useCallback(
    (task: CollaborateTaskData) => {
      if (getInitialTasks) return true;
      try {
        const pageResult = getHostAndPath(window.location.href);
        if (!pageResult) return false;
        const taskResult = getHostAndPath(task.page_url);
        if (!taskResult) return false;
        if (isInIframe()) {
          // check just the paths as the proxy will mess up the host
          return pageResult.path === taskResult.path;
        }
        return (
          pageResult.host === taskResult.host &&
          pageResult.path === taskResult.path
        );
      } catch (_e) {
        return false;
      }
    },
    [getInitialTasks]
  );

  // Filter tasks based on whether they are on the current page and not general tasks
  const filteredTasks = useMemo(() => {
    if (!query.data?.result.tasks) return [];

    let tasks = query.data.result.tasks;

    // Filter by current page if needed
    if (options.forCurrentPageOnly) {
      tasks = tasks.filter((task) => isTaskOnCurrentPage(task));
    }

    // Filter out general tasks if needed
    if (options.filterGeneralTasks) {
      tasks = tasks.filter(
        (task) =>
          !(Array.isArray(task.task_type)
            ? task.task_type.some((type) => type === 'General')
            : task.task_type === 'General')
      );
    }

    return tasks;
  }, [
    query.data,
    isTaskOnCurrentPage,
    options.forCurrentPageOnly,
    options.filterGeneralTasks,
  ]);

  // Find a task by ID
  const findTaskById = useCallback(
    (taskId: string | number) => {
      if (!query.data?.result.tasks) return null;
      return (
        query.data.result.tasks.find((task) => task.task_id === taskId) || null
      );
    },
    [query.data]
  );

  return {
    // Raw query results
    query,
    rawData: query.data,
    currentData: query.currentData,

    // Processed task data
    tasks: query.data?.result.tasks || [],
    filteredTasks,
    hasNoTasks:
      !query.data?.result.tasks || query.data.result.tasks.length === 0,

    // Totals
    totalRecords: query.data?.total_records,
    totalRecordsAssignedToMe: query.data?.total_records_assigned_to_me,

    // Statuses
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,

    // Functions
    refetch: query.refetch,
    isTaskOnCurrentPage,
    findTaskById,

    // Context
    siteId,
    currentPageUrl,
    workspaceId,
    initialTasks: getInitialTasks,

    // Filters (including processed values)
    filters: {
      pageFilter,
      notifiedUsers,
      orderBy,
      taskStatus: taskStatusFilter,
      taskTypes: taskTypesFilter,
      textSearch,
      getInitialTasks,
      // Expose processed filter values for debugging/inspection
      processedTaskStatus: taskStatusForQuery,
      processedTaskTypes: taskTypesForQuery,
    },
  };
};
